// Product-metrics capture (games started/finished, rooms created/joined -
// see server/metrics.js for the accepted event catalog and server/db's
// schema). Deliberately its own module, not sync.js: sync.js's `clientId` is
// random-per-page-load and exists only to dedupe a client's own SSE echo -
// reusing it here would make "distinct devices" uncountable.
import { safeGet, safeSet, safeRemove } from './local-storage.js';

const CLIENT_ID_STORAGE_KEY = 'halveren-metrics-client-id';
const DISABLED_STORAGE_KEY = 'halveren-metrics-disabled';

// Personal opt-out for whoever's dev/testing on production - visiting once
// with ?notrack=1 sets a permanent flag so track() no-ops on this device/
// browser from then on, no matter what page it later lands on; ?notrack=0
// clears it again. One-shot by design: the URL param only ever matters at
// the instant it's read here, every later track() call checks the stored
// flag instead, not the URL.
const notrackParam = new URLSearchParams(location.search).get('notrack');
if (notrackParam === '1') safeSet(DISABLED_STORAGE_KEY, '1');
else if (notrackParam === '0') safeRemove(DISABLED_STORAGE_KEY);

function loadOrCreateClientId() {
    const existing = safeGet(CLIENT_ID_STORAGE_KEY);
    if (existing) return existing;
    const id = `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
    safeSet(CLIENT_ID_STORAGE_KEY, id);
    return id;
}

// Read once per page load, not per track() call - a storage-unavailable
// environment (safeSet is a no-op there) would otherwise mint a fresh random
// id on every single event instead of at least being stable for the page's
// own lifetime.
const clientId = loadOrCreateClientId();

// Exported so app.js can show/hide a small "tracking is off" indicator -
// track() below also uses this, rather than duplicating the safeGet check.
export function isTrackingDisabled() {
    return safeGet(DISABLED_STORAGE_KEY) === '1';
}

// Fire-and-forget: a lost or failed event is not worth retrying (there's no
// user-facing consequence, unlike a game-state sync push), so this never
// awaits its fetch or lets a network error surface anywhere. `keepalive`
// matters specifically for events fired right as a tab backgrounds or a
// player navigates away (e.g. game_finished) - without it the browser can
// cancel an in-flight fetch the instant the page starts unloading.
export function track(name, props = {}) {
    if (isTrackingDisabled()) return;
    try {
        fetch('/api/events', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({
                name,
                ts: Date.now(),
                clientId,
                path: location.pathname,
                props
            })
        }).catch(() => {});
    } catch (e) {}
}
